import { z } from "zod";

/**
 * Class Registration Engine (Phase 2c) — mirrors
 * app/schemas/class_registration.py exactly. Two ClassKind shapes share
 * one ClassSection row (see app/models/class_section.py's module
 * docstring): `program_class` (a Program × age band × single weekday ×
 * slot) and `lunch_extension` (no program/age band, weekday=`tue_thu`,
 * slot=`lunch_11_12`).
 *
 * HARD LEGAL REQUIREMENT (Texas single-skill-exemption compliance): never
 * use "childcare" or "drop-off care" anywhere in this feature's
 * user-facing strings — "classes" / "instruction" only.
 */

export const ClassKindSchema = z.enum(["program_class", "lunch_extension"]);
export type ClassKind = z.infer<typeof ClassKindSchema>;

export const CLASS_KIND_LABELS: Record<ClassKind, string> = {
  program_class: "Class",
  lunch_extension: "Lunch Extension",
};

export const WeekdaySchema = z.enum(["mon", "tue", "wed", "thu", "fri", "tue_thu"]);
export type Weekday = z.infer<typeof WeekdaySchema>;

export const WEEKDAY_LABELS: Record<Weekday, string> = {
  mon: "Monday",
  tue: "Tuesday",
  wed: "Wednesday",
  thu: "Thursday",
  fri: "Friday",
  tue_thu: "Tue/Thu",
};

export const ClassSlotSchema = z.enum(["morning_9_11", "midday_12_2", "afternoon_4_6", "lunch_11_12"]);
export type ClassSlot = z.infer<typeof ClassSlotSchema>;

export const CLASS_SLOT_LABELS: Record<ClassSlot, string> = {
  morning_9_11: "9:00–11:00 AM",
  midday_12_2: "12:00–2:00 PM",
  afternoon_4_6: "4:00–6:00 PM",
  lunch_11_12: "11:00 AM–12:00 PM",
};

export const AgeBandSchema = z.enum(["age_4_6", "age_7_9", "age_10_12"]);
export type AgeBand = z.infer<typeof AgeBandSchema>;

export const AGE_BAND_LABELS: Record<AgeBand, string> = {
  age_4_6: "Ages 4-6",
  age_7_9: "Ages 7-9",
  age_10_12: "Ages 10-12",
};

export const EnrollmentStatusSchema = z.enum(["pending", "active", "waitlisted", "withdrawn", "completed"]);
export type EnrollmentStatus = z.infer<typeof EnrollmentStatusSchema>;

export const ENROLLMENT_STATUS_LABELS: Record<EnrollmentStatus, string> = {
  pending: "Pending",
  active: "Active",
  waitlisted: "Waitlisted",
  withdrawn: "Withdrawn",
  completed: "Completed",
};

export const ENROLLMENT_STATUS_BADGE_VARIANT: Record<
  EnrollmentStatus,
  "default" | "success" | "warning" | "danger" | "info" | "secondary"
> = {
  pending: "warning",
  active: "success",
  waitlisted: "info",
  withdrawn: "danger",
  completed: "secondary",
};

export const PaymentChannelSchema = z.enum(["direct", "esa_tefa"]);
export type PaymentChannel = z.infer<typeof PaymentChannelSchema>;

export const PAYMENT_CHANNEL_LABELS: Record<PaymentChannel, string> = {
  direct: "Pay directly (card/invoice)",
  esa_tefa: "Texas ESA / TEFA (Odyssey marketplace)",
};

export const MediaReleaseChoiceSchema = z.enum(["granted", "declined"]);
export type MediaReleaseChoice = z.infer<typeof MediaReleaseChoiceSchema>;

// ---- Programs ---------------------------------------------------------------

export const ProgramSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  active: z.boolean(),
});
export type Program = z.infer<typeof ProgramSchema>;

export const ProgramListResponseSchema = z.object({ items: z.array(ProgramSchema) });

// ---- Terms --------------------------------------------------------------------

export const TermSchema = z.object({
  id: z.string(),
  name: z.string(),
  start_date: z.string(),
  end_date: z.string(),
  monthly_billing: z.boolean(),
  active: z.boolean(),
});
export type Term = z.infer<typeof TermSchema>;

export const TermListResponseSchema = z.object({ items: z.array(TermSchema) });

// ---- Class sections -------------------------------------------------------------

export const ClassSectionSchema = z.object({
  id: z.string(),
  program_id: z.string().nullable(),
  program_name: z.string().nullable(),
  term_id: z.string(),
  term_name: z.string(),
  kind: ClassKindSchema,
  weekday: WeekdaySchema,
  slot: ClassSlotSchema,
  age_band: AgeBandSchema.nullable(),
  cap: z.number(),
  min_enrollment: z.number(),
  price_cents_monthly: z.number(),
  instructor_name: z.string().nullable(),
  instructor_contact: z.string().nullable(),
  active: z.boolean(),
  active_enrollment_count: z.number(),
  waitlisted_count: z.number(),
  below_minimum: z.boolean(),
});
export type ClassSection = z.infer<typeof ClassSectionSchema>;

export const ClassSectionListResponseSchema = z.object({ items: z.array(ClassSectionSchema) });

// ---- Public class sections (no instructor_contact — PII) ------------------------

export const PublicClassSectionSchema = z.object({
  id: z.string(),
  program_id: z.string().nullable(),
  program_name: z.string().nullable(),
  term_id: z.string(),
  term_name: z.string(),
  kind: ClassKindSchema,
  weekday: WeekdaySchema,
  slot: ClassSlotSchema,
  age_band: AgeBandSchema.nullable(),
  price_cents_monthly: z.number(),
  instructor_name: z.string().nullable(),
  seats_remaining: z.number(),
  waitlist_open: z.boolean(),
});
export type PublicClassSection = z.infer<typeof PublicClassSectionSchema>;

export const PublicClassSectionListResponseSchema = z.object({ items: z.array(PublicClassSectionSchema) });

// ---- Enrollments ------------------------------------------------------------------

export const EnrollmentSchema = z.object({
  id: z.string(),
  section_id: z.string(),
  guardian_contact_id: z.string(),
  child_first_name: z.string(),
  child_last_name: z.string(),
  child_birthdate: z.string(),
  status: EnrollmentStatusSchema,
  payment_channel: PaymentChannelSchema,
  sibling_discount_applied: z.boolean(),
  base_price_cents: z.number(),
  monthly_price_cents: z.number(),
  waiver_at: z.string(),
  disclosure_at: z.string(),
  media_release: MediaReleaseChoiceSchema,
  media_release_at: z.string(),
  consent_text_version: z.number(),
  stripe_subscription_id: z.string().nullable(),
  waitlist_position: z.number().nullable(),
  notes: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string().nullable(),
});
export type Enrollment = z.infer<typeof EnrollmentSchema>;

export const EnrollmentListResponseSchema = z.object({ items: z.array(EnrollmentSchema) });

export const RosterSchema = z.object({
  section: ClassSectionSchema,
  active: z.array(EnrollmentSchema),
  waitlisted: z.array(EnrollmentSchema),
});
export type Roster = z.infer<typeof RosterSchema>;

export const BillEnrollmentResponseSchema = z.object({
  enrollment: EnrollmentSchema,
  billing_mode: z.string(),
});
export type BillEnrollmentResponse = z.infer<typeof BillEnrollmentResponseSchema>;

// ---- Public registration ------------------------------------------------------------

export const PublicRegistrationResponseSchema = z.object({
  enrollment_id: z.string(),
  status: EnrollmentStatusSchema,
  monthly_price_cents: z.number(),
  sibling_discount_applied: z.boolean(),
  waitlist_position: z.number().nullable(),
  // R2-1: surfaced so the enrollment-success panel can tell a family
  // their payment is manual/pending staff follow-up rather than silently
  // saying nothing about billing at all.
  billing_mode: z.string(),
});
export type PublicRegistrationResponse = z.infer<typeof PublicRegistrationResponseSchema>;

// ---- Mutation inputs (admin) --------------------------------------------------------

export interface CreateProgramInput {
  name: string;
  description?: string | null;
}

export interface UpdateProgramInput {
  id: string;
  description?: string | null;
  active?: boolean;
}

export interface CreateTermInput {
  name: string;
  start_date: string;
  end_date: string;
  monthly_billing?: boolean;
}

export interface UpdateTermInput {
  id: string;
  active?: boolean;
}

export interface CreateClassSectionInput {
  term_id: string;
  kind: ClassKind;
  weekday: Weekday;
  slot: ClassSlot;
  price_cents_monthly: number;
  program_id?: string | null;
  age_band?: AgeBand | null;
  cap?: number;
  min_enrollment?: number;
  instructor_name?: string | null;
  instructor_contact?: string | null;
}

export interface UpdateClassSectionInput {
  id: string;
  cap?: number;
  min_enrollment?: number;
  price_cents_monthly?: number;
  instructor_name?: string | null;
  instructor_contact?: string | null;
  active?: boolean;
}

export interface CreateEnrollmentInput {
  section_id: string;
  guardian_contact_id: string;
  child_first_name: string;
  child_last_name: string;
  child_birthdate: string;
  payment_channel: PaymentChannel;
  waiver_ack: boolean;
  disclosure_ack: boolean;
  media_release: MediaReleaseChoice;
  notes?: string | null;
}

export interface UpdateEnrollmentInput {
  id: string;
  notes?: string | null;
}

// ---- Public registration input ------------------------------------------------------

export interface PublicRegistrationInput {
  section_id: string;
  guardian_name: string;
  guardian_email?: string | null;
  guardian_phone?: string | null;
  child_first_name: string;
  child_last_name: string;
  child_birthdate: string;
  payment_channel: PaymentChannel;
  waiver_ack: boolean;
  disclosure_ack: boolean;
  media_release: MediaReleaseChoice;
  notes?: string | null;
  website: string;
}
